import { useRouter } from '@tanstack/react-router';
import { useEffect } from 'react';

import { envConfig } from '#constants/envConfig';
import { reportPushOpen, stripPushOpenParam } from './pushOpen';

const NOTIFICATION_DEEPLINK_PREFIX = 'gg.teamplay.twa://notification';

/**
 * Handles taps on native push notifications. There are two delivery paths, so two listeners:
 *
 *  1. Foreground / app-alive → our FCM service (TeamplayMessagingService) renders the
 *     notification itself with a deep-link content intent
 *     `gg.teamplay.twa://notification?target=<url>`, delivered here as an `appUrlOpen`.
 *  2. Background / killed → the backend sends a hybrid notification+data message, so Google
 *     Play Services renders it in the system tray without waking us. Tapping it launches the
 *     app and Capacitor fires `pushNotificationActionPerformed` with the data payload; we
 *     route via `data.click_action` (the same absolute target URL).
 *
 * The app runs at https://localhost, so we navigate to the target's PATH only (never load the
 * absolute URL). No-op on web. Mount once, high in the tree (inside the router provider).
 */
export const useNativePushTap = () => {
  const router = useRouter();

  useEffect(() => {
    if (!envConfig.IS_NATIVE) {
      return;
    }

    // target is an absolute teamplay.gg URL (click_action) or already a path.
    const navigateToTarget = (target: string | null | undefined) => {
      if (!target) {
        return;
      }
      let path = target;
      let search = '';
      try {
        const parsed = new URL(target);
        search = parsed.search;
        path = `${parsed.pathname}${parsed.search}`;
      } catch {
        // already a relative path
        const at = target.indexOf('?');
        search = at < 0 ? '' : target.slice(at);
      }
      // Отметить открытие до навигации, а не после: дальше начнётся смена роута, и промах здесь
      // означал бы, что на Android счётчик открытий пуст просто потому, что мы не успели.
      // Приложение сюда попадает и холодным стартом, и живым — оба пути проходят через эту
      // функцию, а точка входа client.tsx у нативной сборки открывается по своему адресу и
      // метки не видит.
      reportPushOpen(search);
      if (search) {
        path = path.replace(search, stripPushOpenParam(search));
      }
      if (path.startsWith('/')) {
        router.history.push(path);
      }
    };

    let removeUrl: (() => void) | undefined;
    let removeAction: (() => void) | undefined;
    let active = true;

    (async () => {
      const [{ App }, { PushNotifications }] = await Promise.all([
        import('@capacitor/app'),
        import('@capacitor/push-notifications'),
      ]);

      // Path 1: deep link from our own foreground-rendered notification.
      const urlHandle = await App.addListener('appUrlOpen', ({ url }) => {
        if (!url || !url.startsWith(NOTIFICATION_DEEPLINK_PREFIX)) {
          return;
        }
        try {
          navigateToTarget(new URL(url).searchParams.get('target'));
        } catch {
          // malformed deep link — ignore
        }
      });

      // Path 2: tap on the system-rendered (background/killed) notification.
      const actionHandle = await PushNotifications.addListener(
        'pushNotificationActionPerformed',
        (action) => {
          const data = (action.notification?.data ?? {}) as Record<string, string>;
          navigateToTarget(data.click_action ?? data.target);
        }
      );

      if (active) {
        removeUrl = () => void urlHandle.remove();
        removeAction = () => void actionHandle.remove();
      } else {
        void urlHandle.remove();
        void actionHandle.remove();
      }
    })();

    return () => {
      active = false;
      removeUrl?.();
      removeAction?.();
    };
  }, [router]);
};
